import React, { useState, useRef } from "react";
import { Checkbox } from "./ui/checkbox";
import { Loader2, CheckCircle2, Paperclip, X } from "lucide-react";
import { Link } from "react-router-dom";
import { track } from "../lib/analytics";
import { useToast } from "../hooks/use-toast";
import { services, brand, remodeling, painting, roofline } from "../data/site";

const LEAD_ENDPOINT = "/api/leads";
// Checkbox list on the estimate form. Every name here must also appear in
// ALLOWED_SERVICES in netlify/functions/lead.mjs, or the selection is silently
// dropped from the lead email.
// Remodeling is not in the `services` array (different licence class, its
// own page), so it is appended here. "Not Sure Yet" stays last.
const serviceOptions = services
  .map((s) => s.name)
  .concat(remodeling.enabled ? ["Home Remodeling"] : [])
  .concat(roofline.enabled ? ["Attic & Roofline"] : [])
  .concat(painting.enabled ? ["Painting"] : [])
  .concat(["Not Sure Yet"]);


// English / Spanish strings. Pass lang="es" to render the form in Spanish
// (see src/pages/Espanol.jsx). Service values sent to the CRM stay in English
// so every lead lands in the same buckets.
const STRINGS = {
  en: {
    first: "First Name *", last: "Last Name *", phone: "Phone *", email: "Email *",
    street: "Project Address *", city: "City *", zip: "ZIP *",
    services: "Which Services Are You Interested In? *", message: "Message (optional)",
    when: "Preferred Inspection Date (optional)", window: "Time Window",
    windows: [
      { value: "Any time 7am–7pm", label: "Any time 7am–7pm" },
      { value: "Morning (7am–12pm)", label: "Morning (7am–12pm)" },
      { value: "Afternoon (12pm–7pm)", label: "Afternoon (12pm–7pm)" },
    ],
    whenNote: "We inspect 7 days a week, 7am–7pm, and same-day is often available. Start and end times shift with daylight, so the earliest and latest appointments move through the year with sunrise and sunset. Pick a day and window here — a team member will call to confirm the exact time after you submit.",
    msgPlaceholder: "Tell us about your project...", send: "Send Message", sending: "Sending...",
    files: "Attach Photos Or An Inspection Report (optional)",
    filesHint: "PDF, JPG, PNG or HEIC. Up to 4 files, 4 MB total. Larger inspection reports can be emailed to",
    filesPick: "Choose files",
    filesRemove: (n) => `Remove ${n}`,
    errFileType: ["That file type isn't supported", "Please attach a PDF, JPG, PNG, HEIC or WebP file."],
    errFileCount: ["Too many files", "You can attach up to 4 files. Email the rest and we'll match them up."],
    errFileSize: ["Those files are too large", (email) => `The total has to stay under 4 MB. Email larger inspection reports to ${email} and we'll pick them up.`],
    errFileRead: ["That file couldn't be read", "Please try attaching it again, or email it to us instead."],
    errRequired: ["Please complete required fields", "Name, phone, and email are required."],
    errPhone: ["Check your phone number", "Please enter a 10-digit phone number."],
    errEmail: ["Check your email", "Please enter a valid email address."],
    errAddress: ["Add the project address", "Please enter the street address and city for your project."],
    errZip: ["Check the ZIP code", "Please enter a 5-digit ZIP code."],
    errService: ["Select a service", "Please choose at least one service you're interested in."],
    ok: ["Request received!", "A team member will call to confirm your inspection time. Thank you!"],
    fail: ["Your request didn't go through", (phone) => `Please try again or call us at ${phone}.`],
  },
  es: {
    first: "Nombre *", last: "Apellido *", phone: "Teléfono *", email: "Correo electrónico *",
    street: "Dirección del proyecto *", city: "Ciudad *", zip: "Código postal *",
    services: "¿Qué servicios le interesan? *", message: "Mensaje (opcional)",
    when: "Fecha preferida para la inspección (opcional)", window: "Horario",
    windows: [
      { value: "Any time 7am–7pm", label: "Cualquier hora 7am–7pm" },
      { value: "Morning (7am–12pm)", label: "Mañana (7am–12pm)" },
      { value: "Afternoon (12pm–7pm)", label: "Tarde (12pm–7pm)" },
    ],
    whenNote: "Inspeccionamos los 7 días de la semana, de 7am a 7pm, y muchas veces el mismo día. Los horarios de inicio y cierre cambian con la luz del día, así que las citas más temprano y más tarde se ajustan durante el año con la salida y la puesta del sol. Elija el día y el horario — un miembro del equipo le llamará para confirmar la hora exacta después de enviar.",
    msgPlaceholder: "Cuéntenos sobre su proyecto...", send: "Enviar mensaje", sending: "Enviando...",
    files: "Adjuntar fotos o un informe de inspección (opcional)",
    filesHint: "PDF, JPG, PNG o HEIC. Hasta 4 archivos, 4 MB en total. Los informes más grandes se pueden enviar por correo a",
    filesPick: "Elegir archivos",
    filesRemove: (n) => `Quitar ${n}`,
    errFileType: ["Ese tipo de archivo no es compatible", "Adjunte un archivo PDF, JPG, PNG, HEIC o WebP."],
    errFileCount: ["Demasiados archivos", "Puede adjuntar hasta 4 archivos. Envíe el resto por correo y los juntaremos."],
    errFileSize: ["Los archivos son demasiado grandes", (email) => `El total debe ser menor de 4 MB. Envíe los informes más grandes a ${email} y los recogeremos.`],
    errFileRead: ["No se pudo leer ese archivo", "Intente adjuntarlo de nuevo o envíelo por correo."],
    serviceLabels: {
      "Roofing": "Techos",
      "BINSR Roof Inspections": "Inspecciones BINSR",
      "Tile Underlayment Replacement": "Reemplazo de Fieltro",
      "Foam Roof Coating & Repair": "Recubrimiento y Reparación de Foam",
      "Home Remodeling": "Remodelación de Casas",
      "Attic & Roofline": "Ático y Línea de Techo",
      "Painting": "Pintura",
      "Not Sure Yet": "No Estoy Seguro",
    },
    placeholders: { first: "Juan", last: "García", email: "usted@correo.com", street: "1234 W Main St", city: "Phoenix", zip: "85018" },
    errRequired: ["Complete los campos requeridos", "Nombre, teléfono y correo electrónico son requeridos."],
    errPhone: ["Revise su número de teléfono", "Ingrese un número de 10 dígitos."],
    errEmail: ["Revise su correo electrónico", "Ingrese un correo electrónico válido."],
    errAddress: ["Agregue la dirección del proyecto", "Ingrese la calle y la ciudad de su proyecto."],
    errZip: ["Revise el código postal", "Ingrese un código postal de 5 dígitos."],
    errService: ["Seleccione un servicio", "Elija al menos un servicio que le interese."],
    ok: ["¡Solicitud recibida!", "Un miembro del equipo le llamará para confirmar la hora de su inspección. ¡Gracias!"],
    fail: ["Su solicitud no se envió", (phone) => `Intente de nuevo o llámenos al ${phone}.`],
  },
};


// Attachment limits.
//
// Netlify runs synchronous functions on Lambda, whose invocation payload cap
// is 6 MB REQUEST AND RESPONSE COMBINED — and base64 counts toward it, not the
// raw bytes. Base64 inflates by about 4/3, so a 4 MB raw cap becomes roughly
// 5.3 MB on the wire, leaving around 700 KB of headroom for the rest of the
// form. Raising MAX_TOTAL_BYTES much past this will start failing uploads at
// the platform with an opaque error, so move it only alongside a different
// upload path (direct-to-storage with a signed URL).
//
// Photo-heavy inspection reports often run well past 4 MB, which is why every
// size error points at email instead of just refusing.
const MAX_FILES = 4;
const MAX_TOTAL_BYTES = 4 * 1024 * 1024;
const ALLOWED_FILE_TYPES = new Set([
  "application/pdf", "image/jpeg", "image/png", "image/webp",
  "image/heic", "image/heif",
]);
// Safari and some Android browsers report an empty type for .heic, so the
// extension is checked as a fallback rather than rejecting a valid photo.
const ALLOWED_FILE_EXT = /\.(pdf|jpe?g|png|webp|heic|heif)$/i;

const fileAllowed = (f) =>
  ALLOWED_FILE_TYPES.has((f.type || "").toLowerCase()) || ALLOWED_FILE_EXT.test(f.name || "");

const prettyBytes = (n) =>
  n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;

// FileReader gives "data:<mime>;base64,<payload>" — the function wants only
// the payload, so everything up to and including the comma is dropped.
const readAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || "");
      const i = s.indexOf(",");
      i === -1 ? reject(new Error("unreadable")) : resolve(s.slice(i + 1));
    };
    r.onerror = () => reject(new Error("unreadable"));
    r.readAsDataURL(file);
  });

const LeadForm = ({ dark = false, defaultService = "", defaultCity = "", lang = "en" }) => {
  const { toast } = useToast();
  const t = STRINGS[lang] || STRINGS.en;
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [files, setFiles] = useState([]);   // [{ name, size, type, file }]
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    preferredDate: "",
    preferredWindow: "",
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    street: "",
    city: defaultCity,
    zip: "",
    services: defaultService ? [defaultService] : [],
    message: "",
    company: "", // honeypot, hidden from real visitors
  });

  const idPrefix = dark ? "d-" : "l-";
  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const toggleService = (name) => {
    setForm((p) => ({
      ...p,
      services: p.services.includes(name)
        ? p.services.filter((s) => s !== name)
        : [...p.services, name],
    }));
  };

  // Validate on pick rather than on submit: telling someone a file is too big
  // after they have filled in the whole form is the worst time to tell them.
  const addFiles = (picked) => {
    const incoming = Array.from(picked || []);
    if (!incoming.length) return;

    if (incoming.some((f) => !fileAllowed(f))) {
      toast({ title: t.errFileType[0], description: t.errFileType[1] });
      return;
    }
    const merged = [...files];
    for (const f of incoming) {
      if (!merged.some((x) => x.name === f.name && x.size === f.size)) {
        merged.push({ name: f.name, size: f.size, type: f.type, file: f });
      }
    }
    if (merged.length > MAX_FILES) {
      toast({ title: t.errFileCount[0], description: t.errFileCount[1] });
      return;
    }
    const total = merged.reduce((n, x) => n + x.size, 0);
    if (total > MAX_TOTAL_BYTES) {
      toast({ title: t.errFileSize[0], description: t.errFileSize[1](brand.email) });
      return;
    }
    setFiles(merged);
    // Clearing the input lets the same file be re-picked after a removal.
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (name, size) =>
    setFiles((p) => p.filter((x) => !(x.name === name && x.size === size)));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim() || !form.phone.trim() || !form.email.trim()) {
      toast({ title: t.errRequired[0], description: t.errRequired[1] });
      return;
    }
    if (form.phone.replace(/\D/g, "").length < 10) {
      toast({ title: t.errPhone[0], description: t.errPhone[1] });
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
      toast({ title: t.errEmail[0], description: t.errEmail[1] });
      return;
    }
    if (!form.street.trim() || !form.city.trim()) {
      toast({ title: t.errAddress[0], description: t.errAddress[1] });
      return;
    }
    if (!/^\d{5}$/.test(form.zip.trim())) {
      toast({ title: t.errZip[0], description: t.errZip[1] });
      return;
    }
    if (form.services.length === 0) {
      toast({ title: t.errService[0], description: t.errService[1] });
      return;
    }
    setSubmitting(true);
    try {
      // Files are encoded at submit, not at pick, so holding several large
      // base64 strings in state while someone fills in the rest of the form
      // is avoided.
      let attachments = [];
      if (files.length) {
        try {
          attachments = await Promise.all(
            files.map(async (f) => ({
              filename: f.name,
              content_type: f.type || "application/octet-stream",
              content: await readAsBase64(f.file),
            }))
          );
        } catch {
          setSubmitting(false);
          toast({ title: t.errFileRead[0], description: t.errFileRead[1] });
          return;
        }
      }

      const res = await fetch(LEAD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, attachments, page: window.location.pathname }),
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      setSubmitted(true);
      toast({ title: t.ok[0], description: t.ok[1] });
      track("generate_lead", { services: form.services.join(","), requested_date: form.preferredDate ? "yes" : "no", form_language: lang });
    } catch (err) {
      toast({ title: t.fail[0], description: t.fail[1](brand.phone) });
    } finally {
      setSubmitting(false);
    }
  };

  const labelCls = dark ? "text-white/80" : "text-ink/80";
  const inputCls = dark
    ? "bg-white/5 border-white/15 text-white placeholder:text-white/40 focus:border-brand"
    : "bg-white border-ink/15 text-ink placeholder:text-ink/60 focus:border-brand";

  if (submitted) {
    return (
      <div className={`rounded-2xl p-10 text-center ${dark ? "bg-white/5 border border-white/10" : "bg-secondary border border-ink/10"}`}>
        <CheckCircle2 size={56} className="text-brand mx-auto mb-4" />
        <h3 className={`font-heading text-2xl uppercase tracking-wide mb-2 ${dark ? "text-white" : "text-ink"}`}>Thank You!</h3>
        <p className={dark ? "text-white/70" : "text-ink/70"}>
          Your request has been received. A Pepla specialist will contact you shortly to schedule your free estimate.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
      <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
        <label>
          Company
          <input tabIndex={-1} autoComplete="off" value={form.company} onChange={(e) => update("company", e.target.value)} />
        </label>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={`block text-xs font-medium mb-1.5 uppercase tracking-wide ${labelCls}`} htmlFor={`${idPrefix}lf-first`}>{t.first}</label>
          <input
            id={`${idPrefix}lf-first`} autoComplete="given-name"
            value={form.firstName}
            onChange={(e) => update("firstName", e.target.value)}
            className={`w-full rounded-md border px-4 py-3 text-sm outline-none transition-colors ${inputCls}`}
            placeholder={t.placeholders?.first || "John"}
          />
        </div>
        <div>
          <label className={`block text-xs font-medium mb-1.5 uppercase tracking-wide ${labelCls}`} htmlFor={`${idPrefix}lf-last`}>{t.last}</label>
          <input
            id={`${idPrefix}lf-last`} autoComplete="family-name"
            value={form.lastName}
            onChange={(e) => update("lastName", e.target.value)}
            className={`w-full rounded-md border px-4 py-3 text-sm outline-none transition-colors ${inputCls}`}
            placeholder={t.placeholders?.last || "Smith"}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={`block text-xs font-medium mb-1.5 uppercase tracking-wide ${labelCls}`} htmlFor={`${idPrefix}lf-phone`}>{t.phone}</label>
          <input
            id={`${idPrefix}lf-phone`} type="tel" autoComplete="tel"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            className={`w-full rounded-md border px-4 py-3 text-sm outline-none transition-colors ${inputCls}`}
            placeholder="(602) 555-0123"
          />
        </div>
        <div>
          <label className={`block text-xs font-medium mb-1.5 uppercase tracking-wide ${labelCls}`} htmlFor={`${idPrefix}lf-email`}>{t.email}</label>
          <input
            id={`${idPrefix}lf-email`} type="email" autoComplete="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            className={`w-full rounded-md border px-4 py-3 text-sm outline-none transition-colors ${inputCls}`}
            placeholder={t.placeholders?.email || "you@email.com"}
          />
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className={`block text-xs font-medium mb-1.5 uppercase tracking-wide ${labelCls}`} htmlFor={`${idPrefix}lf-street`}>{t.street}</label>
          <input
            id={`${idPrefix}lf-street`} autoComplete="street-address"
            value={form.street}
            onChange={(e) => update("street", e.target.value)}
            className={`w-full rounded-md border px-4 py-3 text-sm outline-none transition-colors ${inputCls}`}
            placeholder="1234 W Main St"
          />
        </div>
        <div className="grid grid-cols-[minmax(0,1fr)_6.5rem] gap-4">
          <div>
            <label className={`block text-xs font-medium mb-1.5 uppercase tracking-wide ${labelCls}`} htmlFor={`${idPrefix}lf-city`}>{t.city}</label>
            <input
              id={`${idPrefix}lf-city`} autoComplete="address-level2"
              value={form.city}
              onChange={(e) => update("city", e.target.value)}
              className={`w-full rounded-md border px-4 py-3 text-sm outline-none transition-colors ${inputCls}`}
              placeholder="Phoenix"
            />
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1.5 uppercase tracking-wide ${labelCls}`} htmlFor={`${idPrefix}lf-zip`}>{t.zip}</label>
            <input
              id={`${idPrefix}lf-zip`} autoComplete="postal-code" inputMode="numeric" maxLength={5}
              value={form.zip}
              onChange={(e) => update("zip", e.target.value.replace(/\D/g, "").slice(0, 5))}
              className={`w-full rounded-md border px-4 py-3 text-sm outline-none transition-colors ${inputCls}`}
              placeholder="85018"
            />
          </div>
        </div>
      </div>

      <div>
        <label className={`block text-xs font-medium mb-2.5 uppercase tracking-wide ${labelCls}`}>{t.services}</label>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(8.5rem,1fr))] gap-2.5">
          {serviceOptions.map((s) => {
            const active = form.services.includes(s);
            return (
              <button
                type="button"
                key={s}
                onClick={() => toggleService(s)}
                aria-pressed={active}
                className={`flex items-center gap-2 min-h-[3rem] min-w-0 rounded-md border px-3 py-2 text-xs font-medium leading-tight text-left transition-all ${
                  active
                    ? "bg-brand border-brand text-ink"
                    : dark
                    ? "bg-white/5 border-white/15 text-white/75 hover:border-brand"
                    : "bg-white border-ink/15 text-ink/75 hover:border-brand"
                }`}
              >
                <Checkbox decorative checked={active} className={`pointer-events-none data-[state=checked]:bg-ink data-[state=checked]:border-ink ${dark ? "border-white/40" : ""}`} />
                <span className="min-w-0 [overflow-wrap:anywhere]">{t.serviceLabels?.[s] || s}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={`block text-xs font-medium mb-1.5 uppercase tracking-wide ${labelCls}`} htmlFor={`${idPrefix}lf-date`}>{t.when}</label>
          <input
            id={`${idPrefix}lf-date`}
            type="date"
            min={new Date().toISOString().slice(0, 10)}
            value={form.preferredDate}
            onChange={(e) => update("preferredDate", e.target.value)}
            className={`w-full rounded-md border px-4 py-3 text-sm outline-none transition-colors ${inputCls}`}
          />
        </div>
        <div>
          <label className={`block text-xs font-medium mb-1.5 uppercase tracking-wide ${labelCls}`} htmlFor={`${idPrefix}lf-window`}>{t.window}</label>
          <select
            id={`${idPrefix}lf-window`}
            value={form.preferredWindow}
            onChange={(e) => update("preferredWindow", e.target.value)}
            className={`w-full rounded-md border px-4 py-3 text-sm outline-none transition-colors ${inputCls}`}
          >
            {t.windows.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
          </select>
        </div>
      </div>
      <p className={`text-[11px] -mt-2 ${dark ? "text-white/60" : "text-ink/60"}`}>{t.whenNote}</p>

      <div>
        <label className={`block text-xs font-medium mb-1.5 uppercase tracking-wide ${labelCls}`} htmlFor={`${idPrefix}lf-msg`}>{t.message}</label>
        <textarea
          id={`${idPrefix}lf-msg`}
            value={form.message}
          onChange={(e) => update("message", e.target.value)}
          rows={4}
          className={`w-full rounded-md border px-4 py-3 text-sm outline-none transition-colors resize-none ${inputCls}`}
          placeholder={t.msgPlaceholder}
        />
      </div>

      {/* Attachments.
          The native file input is visually hidden rather than display:none, so
          it stays in the tab order and screen readers still announce it; the
          styled <label> is what people see and click, and clicking a label
          focused to its input works by keyboard too. aria-describedby ties the
          size/type hint to the input so it is read out before a file is picked,
          rather than being decorative text nobody hears. */}
      <div>
        <label
          className={`block text-xs font-medium mb-1.5 uppercase tracking-wide ${labelCls}`}
          htmlFor={`${idPrefix}lf-files`}
        >
          {t.files}
        </label>

        <input
          ref={fileInputRef}
          id={`${idPrefix}lf-files`}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/*"
          onChange={(e) => addFiles(e.target.files)}
          aria-describedby={`${idPrefix}lf-files-hint`}
          className="sr-only peer"
        />

        <label
          htmlFor={`${idPrefix}lf-files`}
          className={`flex items-center justify-center gap-2 w-full rounded-md border border-dashed px-4 py-4 text-sm cursor-pointer transition-colors peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand ${
            dark
              ? "border-white/25 text-white/70 hover:border-brand hover:text-brand"
              : "border-ink/20 text-ink/70 hover:border-brand hover:text-brand-dark"
          }`}
        >
          <Paperclip size={16} />
          {t.filesPick}
        </label>

        <p id={`${idPrefix}lf-files-hint`} className={`text-xs mt-2 leading-relaxed ${dark ? "text-white/50" : "text-ink/60"}`}>
          {t.filesHint}{" "}
          <a href={`mailto:${brand.emailRaw}`} className={`underline underline-offset-2 ${dark ? "text-brand" : "text-brand-dark"}`}>
            {brand.email}
          </a>
          .
        </p>

        {files.length > 0 && (
          <ul className="mt-3 space-y-2">
            {files.map((f) => (
              <li
                key={`${f.name}-${f.size}`}
                className={`flex items-center gap-3 rounded-md border px-3 py-2 text-sm ${
                  dark ? "border-white/15 bg-white/5 text-white/80" : "border-ink/10 bg-secondary text-ink/80"
                }`}
              >
                <Paperclip size={14} className="shrink-0 opacity-60" />
                <span className="flex-1 min-w-0 truncate">{f.name}</span>
                {/* /45 and /50 measured 4.26:1 and 3.57:1 against their backgrounds — both
                    under the 4.5:1 AA floor for text this size. */}
                <span className={`shrink-0 text-xs ${dark ? "text-white/65" : "text-ink/70"}`}>{prettyBytes(f.size)}</span>
                <button
                  type="button"
                  onClick={() => removeFile(f.name, f.size)}
                  aria-label={t.filesRemove(f.name)}
                  className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center transition-colors ${
                    dark ? "hover:bg-white/10 text-white/60 hover:text-white" : "hover:bg-ink/5 text-ink/50 hover:text-ink"
                  }`}
                >
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-6 py-4 rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
      >
        {submitting ? <><Loader2 size={18} className="animate-spin" /> {t.sending}</> : t.send}
      </button>
    </form>
  );
};

export default LeadForm;
